import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";

import {StaffAPI} from "@/api/staff";

import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	ERROR_OPERATION,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {checkRequiredFields, getTextField} from "@/utils/functions";
import {useStaffRoles} from "@/utils/hooks";

import "./index.scss";
import {useTranslation} from "react-i18next";

const HandleScoutingStaffPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});

	const {staffRoles} = useStaffRoles({
		setLoading: false,
		handleNotification,
	});
	const [staffUnits, setStaffUnits] = useState([]);
	const getStaffUnits = async () => {
		try {
			const response = await StaffAPI.unitsList();
			setStaffUnits(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	useEffect(() => {
		getStaffUnits();
	}, []);

	const getStaff = async () => {
		setLoading(true);
		try {
			const response = await StaffAPI.getById(id);
			response.staffUnit = {
				value: response.staffUnit.id,
				label: response.staffUnit.name,
			};
			response.staffRole = {
				value: response.staffRole.id,
				label: response.staffRole.name,
			};
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getStaff();
		}
	}, [edit]);

	const getRequiredFields = () => {
		return ["lastName", "staffUnit", "staffRole"];
	};

	const saveStaff = async () => {
		try {
			const errors = checkRequiredFields(getRequiredFields(), fields);
			setErrors(errors);

			if (!errors.length) {
				setLoading(true);

				let body = Object.assign({}, fields);

				body = {
					...fields,
					dateOfBirth: fields.dateOfBirth
						? moment(fields.dateOfBirth).format("YYYY-MM-DD")
						: undefined,
					staffRole: {id: fields.staffRole.value, name: fields.staffRole.label},
					staffRoleId: fields.staffRole.value,
					staffUnit: {id: fields.staffUnit.value, name: fields.staffUnit.label},
					staffUnitId: fields.staffUnit.value,
					prospect: true,
				};

				const response = edit
					? await StaffAPI.update(id, body)
					: await StaffAPI.create(body);

				setTimeout(() => {
					navigate(`/scouting/staffs/${response?.id || id}`);
					setLoading(false);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveStaff();
	};

	const goBack = () => {
		navigate("/scouting/staffs");
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	const staffLabel =
		fields?.firstName || fields?.lastName
			? (fields.firstName || "") + " " + (fields.lastName || "")
			: "";

	const breadcrumbsElements = [{label: "Staff", path: "/scouting/staffs"}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: staffLabel,
				path: "/scouting/staffs/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<VLFCard
					title="Informazioni principali"
					content={
						<div className="row no-margin">
							<div className="col-12 col-md-6 padding-small">
								{getTextField({
									...defaultTextField,
									fieldKey: "firstName",
									label: t("name"),
								})}
							</div>
							<div className="col-12 col-md-6 padding-small">
								{getTextField({
									...defaultTextField,
									fieldKey: "lastName",
									label: "Cognome",
									required: true,
								})}
							</div>
							<div className="col-sm-12 col-md-6 padding-small">
								<VLFDatePicker
									label="Data di nascita"
									value={fields.dateOfBirth}
									onChange={(value) => {
										setErrors([]);
										setFields({...fields, dateOfBirth: value});
									}}
									size="small"
								/>
							</div>
							<div className="col-sm-12 col-md-6 padding-small">
								<VLFSelectNew
									options={staffUnits.map((su) => {
										return {
											value: su.id,
											label: su.name,
										};
									})}
									value={fields.staffUnit}
									onChange={(newValue) => {
										setErrors([]);
										setFields({...fields, staffUnit: newValue});
									}}
									label="Area"
									required
									helperText={
										errors.indexOf("staffUnit") !== -1 ? REQUIRED_FIELD : ""
									}
									error={errors.indexOf("staffUnit") !== -1}
								/>
							</div>
							<div className="col-sm-12 col-md-6 padding-small">
								<VLFSelectNew
									options={staffRoles.map((sr) => {
										return {
											value: sr.id,
											label: sr.name,
										};
									})}
									value={fields.staffRole}
									onChange={(newValue) => {
										setErrors([]);
										setFields({...fields, staffRole: newValue});
									}}
									label="Ruolo"
									required
									helperText={
										errors.indexOf("staffRole") !== -1 ? REQUIRED_FIELD : ""
									}
									error={errors.indexOf("staffRole") !== -1}
								/>
							</div>
						</div>
					}
				/>
			</div>
		</Page>
	);
};

export default HandleScoutingStaffPage;
